import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { useEventSpotCheckMonthlyUsages } from './useEventSpotCheckMonthlyUsage';
import { useMultiScheduledSpotCheckMonthlyUsages } from './useScheduledSpotCheckMonthlyUsage';

export default function useSpotChecksMonthlyConsumption({
  eventSpotChecks,
  scheduledSpotChecks,
}: {
  eventSpotChecks: AiNotification[];
  scheduledSpotChecks: ScheduledActivityEntity[];
}) {
  const { monthlyUsages: scheduledSpotChecksUsages, loading: scheduledSpotChecksLoading } =
    useMultiScheduledSpotCheckMonthlyUsages(scheduledSpotChecks);

  const { monthlyUsages: eventSpotCheckMonthlyUsages, loading: eventSpotChecksLoading } =
    useEventSpotCheckMonthlyUsages(eventSpotChecks);

  const totalEventSpotCheckMonthlyUsage =
    (eventSpotCheckMonthlyUsages &&
      Object.values(eventSpotCheckMonthlyUsages).reduce((acc, usage) => acc + usage, 0)) ??
    0;

  const totalScheduledSpotCheckMonthlyUsage = Object.values(scheduledSpotChecksUsages)
    .filter((usage): usage is number => typeof usage === 'number')
    .reduce((acc, usage) => acc + usage, 0);

  const monthlySpotCheckConsumption =
    totalEventSpotCheckMonthlyUsage + totalScheduledSpotCheckMonthlyUsage;

  const loading = scheduledSpotChecksLoading || eventSpotChecksLoading;

  const averageDailyConsumption = monthlySpotCheckConsumption / 30;

  const remainingDaysInMonth = getRemainingDaysInMonth();

  const restOfMonthConsumption = averageDailyConsumption * remainingDaysInMonth;

  return {
    monthlySpotCheckConsumption,
    averageDailyConsumption,
    restOfMonthConsumption,
    loading,
  };
}

function getRemainingDaysInMonth() {
  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return lastDayOfMonth.getDate() - today.getDate();
}
